import React, { useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { StatementInvoice } from '../model';
import { CollectionInvoicePdf } from '../../clients/model';
import { formatDollar, formatOptionalDate } from '../../clients/utils/format';
import { useInvoicePdfUpload } from '../../clients/hooks/useInvoicePdfUpload';
import { useInvoicePdfDelete } from '../../clients/hooks/useInvoicePdfDelete';
import { groupInvoicesByDepartment } from '../../clients/utils/groupInvoices';
import DepartmentGroupedInvoiceSections from '../../shared/invoices/DepartmentGroupedInvoiceSections';
import InvoicePdfCell from '../../shared/invoices/InvoicePdfCell';

interface Props {
  invoices: StatementInvoice[];
  location: string;
  acctNo: string;
  onUploaded: (applyTo: string, pdf: CollectionInvoicePdf) => void;
  onDeleted: (applyTo: string, pdfId: number) => void;
}

const buildColumns = (
  triggerUpload: (applyTo: string) => void,
  uploadingApplyTo: string | null,
  canUpload: boolean,
  deletePdf: (applyTo: string, pdfId: number, filename: string) => void,
  deletingPdfId: number | null,
) => [
  { name: 'Invoice #', selector: (row: StatementInvoice) => row.apply_to, width: '90px' },
  {
    name: 'Invoice Date',
    selector: (row: StatementInvoice) => formatOptionalDate(row.invoice_date),
    width: '120px',
  },
  {
    name: 'Age',
    selector: (row: StatementInvoice) => (row.age_days == null ? '' : String(row.age_days)),
    width: '70px',
  },
  {
    name: 'Amount',
    cell: (row: StatementInvoice) => <Typography variant="body2">{formatDollar(row.amount)}</Typography>,
    right: true,
    width: '120px',
  },
  {
    name: 'Balance',
    cell: (row: StatementInvoice) => (
      <Typography
        variant="body2"
        sx={{
          color: row.balance > 0 ? 'error.main' : row.balance < 0 ? 'success.main' : 'text.primary',
        }}
      >
        {formatDollar(row.balance)}
      </Typography>
    ),
    right: true,
    width: '120px',
  },
  {
    name: 'PO',
    selector: (row: StatementInvoice) => row.customer_po || '',
    width: '150px',
  },
  {
    name: 'Reference',
    selector: (row: StatementInvoice) => row.reference || '',
    width: '150px',
  },
  {
    name: 'PDF',
    grow: 2,
    cell: (row: StatementInvoice) =>
      row.has_pdf && row.pdf ? (
        <InvoicePdfCell pdf={row.pdf} applyTo={row.apply_to} deletingPdfId={deletingPdfId} onDelete={deletePdf} />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<UploadFileIcon />}
            disabled={!canUpload || uploadingApplyTo === row.apply_to}
            onClick={() => triggerUpload(row.apply_to)}
          >
            {uploadingApplyTo === row.apply_to ? 'Uploading…' : 'Upload Invoice'}
          </Button>
        </Box>
      ),
  },
];

const StatementInvoicesTable: React.FC<Props> = ({ invoices, location, acctNo, onUploaded, onDeleted }) => {
  const { fileInputRef, triggerUpload, handleFileSelected, uploadingApplyTo, canUpload } = useInvoicePdfUpload({
    location,
    acctNo,
    onUploaded: ({ applyTo, pdf }) => onUploaded(applyTo, pdf),
  });
  const { deletePdf, deletingPdfId } = useInvoicePdfDelete({ onDeleted });

  const columns = useMemo(
    () => buildColumns(triggerUpload, uploadingApplyTo, canUpload, deletePdf, deletingPdfId),
    [triggerUpload, uploadingApplyTo, canUpload, deletePdf, deletingPdfId],
  );

  const groups = useMemo(() => groupInvoicesByDepartment(invoices, (inv) => inv.balance), [invoices]);
  const totalBalance = useMemo(() => invoices.reduce((sum, invoice) => sum + invoice.balance, 0), [invoices]);
  const namedDepartmentCount = useMemo(() => groups.filter((group) => !group.isUngrouped).length, [groups]);
  const hasUngroupedGroup = useMemo(() => groups.some((group) => group.isUngrouped), [groups]);

  const summaryText = (
    <>
      <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>
        {invoices.length}
      </Box>{' '}
      statement {invoices.length === 1 ? 'invoice' : 'invoices'} in{' '}
      <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>
        {namedDepartmentCount}
      </Box>{' '}
      {namedDepartmentCount === 1 ? 'department' : 'departments'}
      {hasUngroupedGroup ? ' + ungrouped' : ''} with balance of{' '}
      <Box component="span" sx={{ color: 'error.main', fontWeight: 700 }}>
        {formatDollar(totalBalance)}
      </Box>
    </>
  );

  return (
    <>
      <input ref={fileInputRef} type="file" accept="application/pdf,.pdf" hidden onChange={handleFileSelected} />
      {invoices.length === 0 ? (
        <Box sx={{ p: 3, color: 'text.secondary' }}>No outstanding items for this client.</Box>
      ) : (
        <DepartmentGroupedInvoiceSections
          groups={groups}
          columns={columns}
          summaryText={summaryText}
          emptyMessage="No outstanding items for this client."
        />
      )}
    </>
  );
};

export default StatementInvoicesTable;
